import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating views
import { getCities } from 'models/cities'; // Importing getCities function from cities model
import { getTags } from 'models/tags'; // Importing getTags function from tags model
import { getPositions } from 'models/positions'; // Importing getPositions function from positions model
import 'webix/photo'; // Importing webix photo module
import 'webix/tinymce/tinymce'; // Importing TinyMCE editor from webix
import {
    profile, // User profile configuration
    devConfig, // Development configuration settings
    countries, // List of countries
    paymentSystem, // Payment system configuration
    cashDesks, // Cash desk configuration
    agentList, // List of agents
} from 'config/config'; // Importing various configuration settings

/**
 * Class representing the filter withdrawal request view.
 * @extends JetView
 */
export default class FilterWithdrawalRequestView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization function
        const lang = this.app.getService('locale').getLang(); // Get current language
        const left = {
            // Left section configuration
            margin: 5,
            id: 'a1',
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'text', // Text input for date picker
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange',
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                        },
                        {
                            view: 'datepicker', // Date picker component
                            name: 'Date',
                            id: 'Date',
                            stringResult: true,
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for the date picker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: moment().format('YYYY-MM-DD'), // Default value set to today's date
                            format: '%d %M %Y', // Date format
                            suggest: {
                                type: 'calendar',
                                body: {
                                    maxDate: new Date(), // Maximum selectable date is today
                                },
                            },
                        },
                        {
                            view: 'text', // Text input for player ID
                            name: 'PlayerId',
                            minWidth: 200,
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Cliente'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('ID Cliente'), // Placeholder text
                            invalidMessage: _('Client ID is required'), // Validation message
                        },
                        {
                            view: 'text', // Text input for withdrawal ID
                            name: 'WithdrawId',
                            type: 'number',
                            minWidth: 200,
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Retiro'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('ID Retiro'), // Placeholder text
                            invalidMessage: _('Withdraw ID is required'), // Validation message
                        },
                        {
                            view: 'richselect', // Dropdown for state selection
                            name: 'State',
                            minWidth: 200,
                            label: "<span class='icon-state'></span>" + _('Estado'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                // Options for the dropdown
                                {
                                    id: '',
                                    value: _('Todos'), // Option for all states
                                },
                                {
                                    id: 'A',
                                    value: _('Activo'), // Active state
                                },
                                {
                                    id: 'I',
                                    value: _('Inactivo'), // Inactive state
                                },
                                {
                                    id: 'R',
                                    value: _('Rechazado'), // Rejected state
                                },
                                {
                                    id: 'P',
                                    value: _('Pendiente'), // Pending state
                                },
                                {
                                    id: 'E',
                                    value: _('Eliminado'), // Deleted state
                                },
                                {
                                    id: 'D',
                                    value: _('Devueltas'), // Returned state
                                },
                                {
                                    id: 'PS',
                                    value: _('Pendiente por Sistema'), // Pending by system
                                },
                                {
                                    id: 'AS',
                                    value: _('Aprobado por Sistema'), // Approved by system
                                },
                                {
                                    id: 'RS',
                                    value: _('Rechazado por Sistema'), // Rejected by system
                                },
                            ],
                        },
                    ],
                },
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'text', // Text input for IP address
                            name: 'Ip',
                            minWidth: 200,
                            label: "<span class='icon-ip'></span>" + _('Dirección IP'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Dirección IP'), // Placeholder text
                        },
                        {
                            view: 'checkbox', // Checkbox for filtering by withdrawal date
                            label: _('Por fecha de retiro pagado'),
                            labelPosition: 'top',
                            name: 'ByAllowDate',
                            value: 0,
                            minWidth: 200,
                        },
                        {
                            view: 'richselect', // Dropdown for payment methods
                            name: 'PaymentMethods',
                            minWidth: 200,
                            label: "<span class='icon-credit-card'></span>" + _('Formas de Pago'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                // Options for the dropdown
                                {
                                    id: '0',
                                    value: _('Todos'), // Option for all payment methods
                                },
                                {
                                    id: '1',
                                    value: _('Efectivo'), // Cash payment method
                                },
                                {
                                    id: '2',
                                    value: _('Cuenta Bancaria'), // Bank account payment method
                                },
                                {
                                    id: '3',
                                    value: _('Giro Bancario'), // Bank transfer payment method
                                },
                            ],
                        },
                        {
                            hidden: devConfig.getPermAction('filterByBetShop', 'Add'), // Conditional visibility based on permissions
                            css: 'prm-filterByBetShop',
                            height: 1,
                        },
                        {
                            view: 'combo', // Combo box for selecting bet shop
                            name: 'BetShopId',
                            minWidth: 200,
                            label: "<span class='icon-shop'></span>" + _('Punto de Venta'), // Label for the combo box
                            hidden: !devConfig.getPermAction('filterByBetShop', 'Add'), // Conditional visibility based on permissions
                            css: 'prm-filterByBetShop',
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            id: 'BetShopId',
                            options: {
                                body: {
                                    dataFeed: function (text) {
                                        // Data feed function for dynamic loading
                                        var vthis = this;
                                        if (text.length > 2) {
                                            // Trigger data load if input length is greater than 2
                                            webix
                                                .ajax()
                                                .headers({
                                                    'Content-type': 'application/json',
                                                })
                                                .post(
                                                    devConfig.geturlApi() + 'es/Select/BetShopsSelect',
                                                    '{"Filter":"' + text + '"}', // Sending filter text
                                                    {
                                                        error: function (text, data, XmlHttpRequest) {
                                                            webix.message(
                                                                'Error! ', // Error message on failure
                                                            );
                                                        },
                                                        success: function (text, data, XmlHttpRequest) {
                                                            var resp = JSON.parse(
                                                                text, // Parsing response
                                                            );
                                                            if (
                                                                !resp.HasError // Check for errors in response
                                                            ) {
                                                                vthis.parse(
                                                                    resp.Data, // Parsing data into the combo
                                                                );
                                                            }
                                                        },
                                                    },
                                                );
                                        }
                                    },
                                },
                            },
                        },
                    ],
                },
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'richselect', // Dropdown for payment method bank accounts
                            name: 'PaymentMethodBankAccounts',
                            minWidth: 200,
                            label: "<span class='icon-credit-card'></span>" + _('Método de pago cuentas bancarias'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                // Options for the dropdown
                                {
                                    id: '0',
                                    value: _('Todos'), // Option for all bank accounts
                                },
                                {
                                    id: '1',
                                    value: _('Pagado físicamente'), // Physically paid option
                                },
                                {
                                    id: '2',
                                    value: _('Pagado por sistema'), // System paid option
                                },
                            ],
                        },
                        {
                            view: 'text', // Text input for minimum value
                            name: 'ValueMinimum',
                            type: 'number',
                            minWidth: 200,
                            label: "<span class='icon-cash'></span>" + _('Valor Mínimo'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Valor Mínimo'), // Placeholder text
                        },
                        {
                            view: 'text', // Text input for maximum value
                            name: 'ValueMaximum',
                            type: 'number',
                            minWidth: 200,
                            label: "<span class='icon-cash'></span>" + _('Valor Máximo'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Valor Máximo'), // Placeholder text
                        },
                        {
                            hidden: devConfig.getPermAction('filterByCountry', 'Add'), // Conditional visibility based on permissions
                            css: 'prm-filterByCountry',
                            height: 1,
                        },
                        {
                            view: 'richselect', // Dropdown for country selection
                            name: 'CountrySelect',
                            id: 'CountrySelect',
                            minWidth: 200,
                            label: "<span class='icon-language'></span>" + _('País'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: {
                                body: {
                                    template: function (obj) {
                                        // Template for displaying country flags
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data for the dropdown
                                },
                            },
                            hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'), // Conditional visibility based on permissions
                            css: 'prm-filterByCountry',
                        },
                        {
                            view: 'richselect', // Dropdown for secondary country selection
                            name: 'CountrySelect2',
                            minWidth: 200,
                            label: "<span class='icon-language'></span>" + _('País'), // Label for the dropdown
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: profile.Settings.ReportCountry, // Default value from profile settings
                            readonly: true, // Read-only field
                            options: {
                                body: {
                                    template: function (obj) {
                                        // Template for displaying country flags
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data for the dropdown
                                },
                            },
                            hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'), // Conditional visibility based on permissions
                            css: 'prm-filterByCountry',
                            css: 'country-hide', // Additional CSS class for hiding
                        },
                    ],
                },
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'radio', // Radio buttons for type selection
                            name: 'Type',
                            id: 'Type',
                            label: _('Tipo'), // Label for the radio group
                            labelPosition: 'top',
                            height: screen.width < 500 ? 100 : 0, // Conditional height based on screen width
                            value: 0, // Default selected value
                            minWidth: 200,
                            options: [
                                // Options for the radio group
                                { id: 0, value: _('Detallado') }, // Detailed option
                                { id: 1, value: _('Totales') }, // Total option
                            ],
                        },
                        {
                            view: 'text', // Text input for system ID
                            name: 'SystemId',
                            minWidth: 200,
                            label: "<span class='icon-id'></span>" + _('Id Sistema'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Id Sistema'), // Placeholder text
                        },
                        {
                            view: 'combo', // Combo box for selecting bank
                            name: 'BankId',
                            minWidth: 200,
                            label: "<span class='icon-shop'></span>" + _('Banco'), // Label for the combo box
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: {
                                body: {
                                    dataFeed: function (text) {
                                        // Data feed function for dynamic loading
                                        var vthis = this;
                                        if (text.length > 2) {
                                            // Trigger data load if input length is greater than 2
                                            webix
                                                .ajax()
                                                .headers({
                                                    'Content-type': 'application/json',
                                                })
                                                .post(
                                                    devConfig.geturlApi() + 'es/Select/BanksSelect',
                                                    '{"Filter":"' + text + '"}', // Sending filter text
                                                    {
                                                        error: function (text, data, XmlHttpRequest) {
                                                            webix.message(
                                                                'Error! ', // Error message on failure
                                                            );
                                                        },
                                                        success: function (text, data, XmlHttpRequest) {
                                                            var resp = JSON.parse(
                                                                text, // Parsing response
                                                            );
                                                            if (
                                                                !resp.HasError // Check for errors in response
                                                            ) {
                                                                vthis.parse(
                                                                    resp.Data, // Parsing data into the combo
                                                                );
                                                            }
                                                        },
                                                    },
                                                );
                                        }
                                    },
                                },
                            },
                        },
                        { id: 'grav', gravity: 1, minWidth: 200 }, // Gravity for layout management
                    ],
                },
                { id: 'grav2', cols: [] }, // Empty column for layout management
            ],
        };
        const left2 = {
            // Second left section configuration
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            view: 'text', // Text input for deletion date
                            name: 'DateDelete',
                            id: 'DateDelete',
                            css: 'reportrange2',
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de Eliminación'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            on: {
                                onFocus: function (o, e, v) {
                                    // Event on focus
                                    devConfig.setDateModal(lang); // Set date modal based on language
                                },
                            },
                        },
                        {
                            view: 'datepicker', // Date picker for deletion date
                            name: 'DateDelete2',
                            id: 'DateDelete2',
                            stringResult: true,
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de Eliminación'), // Label for the date picker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: moment().format('YYYY-MM-DD'), // Default value set to today's date
                            format: '%d %M %Y', // Date format
                            suggest: {
                                type: 'calendar',
                                body: {
                                    maxDate: new Date(), // Maximum selectable date is today
                                },
                            },
                        },
                        {
                            view: 'text', // Text input for payment date
                            name: 'DatePay',
                            id: 'DatePay',
                            css: 'reportrange3',
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de pago'), // Label for the input
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            on: {
                                onFocus: function (o, e, v) {
                                    // Event on focus
                                    devConfig.setDateModal(lang); // Set date modal based on language
                                },
                            },
                        },
                        {
                            view: 'datepicker', // Date picker for payment date
                            name: 'DatePay2',
                            id: 'DatePay2',
                            stringResult: true,
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de pago'), // Label for the date picker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: moment().format('YYYY-MM-DD'), // Default value set to today's date
                            format: '%d %M %Y', // Date format
                            suggest: {
                                type: 'calendar',
                                body: {
                                    maxDate: new Date(), // Maximum selectable date is today
                                },
                            },
                        },
                    ],
                },
            ],
        };
        const upper_section = {
            // Upper section configuration
            css: 'father-container',
            rows: [
                {
                    margin: 10,
                    cols: [left], // Including the left section
                },
            ],
        };
        const upper_section2 = {
            // Upper section 2 configuration
            css: 'father-container',
            height: screen.width < 500 ? 150 : 0, // Conditional height based on screen width
            rows: [
                {
                    margin: 10,
                    cols: [left2], // Including the second left section
                },
            ],
        };
        /**
         * Converts a file to a Base64 string.
         * @param {File} file - The file to convert.
         * @returns {Promise<string>} A promise that resolves to the Base64 string.
         */
        function getBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader(); // FileReader instance for reading files
                reader.readAsDataURL(file); // Reading file as Data URL
                reader.onload = () => resolve(reader.result); // Resolving promise with result
                reader.onerror = (error) => reject(error); // Rejecting promise on error
            });
        }
        var base64CSV = ''; // Variable to hold the Base64 encoded CSV data
        const massiveRejection = webix.ui({
            // UI configuration for massive rejection window
            view: 'window',
            id: 'MassiveRejection',
            fullscreen: screen.width < 500 ? true : false, // Fullscreen mode based on screen width
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Rechazos masivo') }, // Label for the window header
                    {
                        view: 'icon',
                        icon: 'wxi-close',
                        click: function () {
                            this.getTopParentView().hide(); // Close the window on click
                        },
                    },
                ],
            },
            position: 'center', // Center position for the window
            modal: true, // Modal window
            body: {
                view: 'form',
                id: 'addMassiveRejectionForm', // Form ID for massive rejection
                elements: [
                    {
                        margin: 10,
                        rows: [
                            {
                                view: 'uploader', // Uploader component for CSV file
                                id: 'uploader',
                                value: _('Cargar archivo CSV'), // Label for the uploader
                                on: {
                                    onBeforeFileAdd: function (upload) {
                                        // Event before file is added
                                        getBase64(upload.file).then(
                                            (data) => (base64CSV = data), // Convert file to Base64
                                        );
                                        $$('uploader').define(
                                            'label',
                                            upload.name, // Update uploader label with file name
                                        );
                                        $$('uploader').refresh(); // Refresh uploader
                                    },
                                },
                            },
                            {
                                view: 'textarea', // Text area for description
                                labelPosition: 'top',
                                label: _('Descripción:'), // Label for the text area
                                name: 'Description',
                                labelWidth: 150,
                                required: true, // Required field
                                height: 150,
                            },
                        ],
                    },
                    {
                        cols: [
                            {
                                view: 'button', // Cancel button
                                value: _('Cancelar'),
                                click: function () {
                                    this.getTopParentView().hide(); // Close the window on click
                                    $$('addMassiveRejectionForm').setValues(
                                        $$('addMassiveRejectionForm').getCleanValues(), // Reset form values
                                    );
                                    $$('uploader').define(
                                        'label',
                                        _('Cargar archivo CSV'), // Reset uploader label
                                    );
                                    $$('uploader').refresh(); // Refresh uploader
                                    base64CSV = ''; // Clear Base64 variable
                                },
                            },
                            {
                                view: 'button', // Save button
                                type: 'form',
                                value: _('Guardar'),
                                click: function ($e) {
                                    var values = $$('addMassiveRejectionForm').getValues(); // Get form values
                                    if (
                                        $$('addMassiveRejectionForm').validate() && // Validate form
                                        base64CSV !== '' // Check if CSV data is present
                                    ) {
                                        webix
                                            .ajax()
                                            .headers({
                                                'Content-type': 'application/json',
                                            })
                                            .post(
                                                devConfig.geturlApi() + 'Client/MassiveRejection',
                                                '{"CSV":"' +
                                                    base64CSV +
                                                    '","Type":"1' +
                                                    '","Description":"' +
                                                    values.Description +
                                                    '"}', // Sending data to server
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Error! ', // Error message on failure
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        text = JSON.parse(text); // Parsing response
                                                        if (text.HasError) {
                                                            webix.message(
                                                                'Error: ' + text.AlertMessage, // Display error message
                                                            );
                                                        } else {
                                                            webix.message(
                                                                'Rechazo masivo creado', // Success message
                                                            );
                                                        }
                                                    },
                                                },
                                            );
                                        this.getTopParentView().hide(); // Close the window on success
                                    } else {
                                        webix.message({
                                            title: _('¡Mensaje!'),
                                            type: 'alert-error',
                                            text: _('Completa los campos...'), // Alert message for incomplete fields
                                        });
                                    }
                                },
                            },
                        ],
                    },
                ],
                elementsConfig: {
                    on: {
                        onChange: function () {
                            this.validate(); // Validate form on change
                        },
                    },
                },
            },
        });
        const massiveApproval = webix.ui({
            // UI configuration for massive approval window
            view: 'window',
            id: 'MassiveApproval',
            fullscreen: screen.width < 500 ? true : false, // Fullscreen mode based on screen width
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Aprobaciones masivas') }, // Label for the window header
                    {
                        view: 'icon',
                        icon: 'wxi-close',
                        click: function () {
                            this.getTopParentView().hide(); // Close the window on click
                        },
                    },
                ],
            },
            position: 'center', // Center position for the window
            modal: true, // Modal window
            body: {
                view: 'form',
                id: 'addMassiveApprovalForm', // Form ID for massive approval
                elements: [
                    {
                        margin: 10,
                        rows: [
                            {
                                view: 'uploader', // Uploader component for CSV file
                                id: 'uploader',
                                value: _('Cargar archivo CSV'), // Label for the uploader
                                on: {
                                    onBeforeFileAdd: function (upload) {
                                        // Event before file is added
                                        getBase64(upload.file).then(
                                            (data) => (base64CSV = data), // Convert file to Base64
                                        );
                                        $$('uploader').define(
                                            'label',
                                            upload.name, // Update uploader label with file name
                                        );
                                        $$('uploader').refresh(); // Refresh uploader
                                    },
                                },
                            },
                            {
                                view: 'textarea', // Text area for description
                                labelPosition: 'top',
                                label: _('Descripción:'), // Label for the text area
                                name: 'Description',
                                labelWidth: 150,
                                required: true, // Required field
                                height: 150,
                            },
                        ],
                    },
                    {
                        cols: [
                            {
                                view: 'button', // Cancel button
                                value: _('Cancelar'),
                                click: function () {
                                    this.getTopParentView().hide(); // Close the window on click
                                    $$('addMassiveApprovalForm').setValues(
                                        $$('addMassiveApprovalForm').getCleanValues(), // Reset form values
                                    );
                                    $$('uploader').define(
                                        'label',
                                        _('Cargar archivo CSV'), // Reset uploader label
                                    );
                                    $$('uploader').refresh(); // Refresh uploader
                                    base64CSV = ''; // Clear Base64 variable
                                },
                            },
                            {
                                view: 'button', // Save button
                                type: 'form',
                                value: _('Guardar'),
                                click: function ($e) {
                                    var values = $$('addMassiveApprovalForm').getValues(); // Get form values
                                    if (
                                        $$('addMassiveApprovalForm').validate() && // Validate form
                                        base64CSV !== '' // Check if CSV data is present
                                    ) {
                                        webix
                                            .ajax()
                                            .headers({
                                                'Content-type': 'application/json',
                                            })
                                            .post(
                                                devConfig.geturlApi() + 'Client/MassiveApproval',
                                                '{"CSV":"' +
                                                    base64CSV +
                                                    '","Type":"1' +
                                                    '","Description":"' +
                                                    values.Description +
                                                    '"}', // Sending data to server
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Error! ', // Error message on failure
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        text = JSON.parse(text); // Parsing response
                                                        if (text.HasError) {
                                                            webix.message(
                                                                'Error: ' + text.AlertMessage, // Display error message
                                                            );
                                                        } else {
                                                            webix.message(
                                                                'Aprobación masiva creada', // Success message
                                                            );
                                                        }
                                                    },
                                                },
                                            );
                                        this.getTopParentView().hide(); // Close the window on success
                                    } else {
                                        webix.message({
                                            title: _('¡Mensaje!'),
                                            type: 'alert-error',
                                            text: _('Completa los campos...'), // Alert message for incomplete fields
                                        });
                                    }
                                },
                            },
                        ],
                    },
                ],
                elementsConfig: {
                    on: {
                        onChange: function () {
                            this.validate(); // Validate form on change
                        },
                    },
                },
            },
        });
        const buttons = {
            // Button configuration for the main form
            margin: 10,
            cols: [
                {},
                {
                    view: 'button', // Button for massive approval
                    value: _('Aprobación masiva'),
                    autowidth: true,
                    hidden: !devConfig.getPermAction('massiveApprovalPv', 'Add'), // Conditional visibility based on permissions
                    click: () => {
                        massiveApproval.show(); // Show massive approval window
                    },
                },
                {
                    view: 'button', // Button for massive rejection
                    value: _('Rechazo masivo'),
                    autowidth: true,
                    hidden: !devConfig.getPermAction('massiveRejectionPv', 'Add'), // Conditional visibility based on permissions
                    click: () => {
                        massiveRejection.show(); // Show massive rejection window
                    },
                },
                {
                    view: 'button', // Button to clear form
                    value: _('Limpiar'),
                    autowidth: true,
                    click: () => {
                        $$('myForm').setValues($$('myForm').getCleanValues()); // Reset form values
                    },
                },
                {
                    view: 'button', // Button to consult/filter data
                    value: _('Consultar'),
                    hotkey: 'enter', // Enter key to trigger
                    type: 'form',
                    autowidth: true,
                    click: () => {
                        const filterdata = this.getRoot().getValues(); // Get filter data from the form
                        if (devConfig.getCountryReport()) {
                            filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country based on profile settings
                        }
                        delete filterdata['CountrySelect2']; // Remove unnecessary field
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove date field if not applicable
                            delete filterdata['DateDelete2']; // Remove date delete field if not applicable
                            delete filterdata['DatePay2']; // Remove date pay field if not applicable
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove datepicker if applicable
                                delete filterdata['DateDelete']; // Remove date delete field if applicable
                                delete filterdata['DatePay']; // Remove date pay field if applicable
                            }
                        }
                        filterdata.TypeUser = '1'; // Set user type
                        this.app.callEvent('bonusReport:filter', [filterdata]); // Trigger filter event
                        $$('b5').collapse(); // Collapse accordion section
                        $$('a5').scrollTo(100, 0); // Scroll to specific position
                    },
                },
            ],
        };
        const buttons2 = {
            // Button configuration for the secondary form
            padding: 10,
            cols: [
                {
                    view: 'button', // Button to clear secondary form
                    value: _('Limpiar'),
                    click: () => {
                        $$('myForm2').setValues($$('myForm2').getCleanValues()); // Reset form values
                        $$('CountrySelect').setValue(
                            $$('myForm2').getCleanValues().CountrySelect, // Set country select value
                        );
                        $$('Datepicker').setValue(''); // Reset datepicker value
                    },
                },
                {
                    view: 'button', // Button to consult/filter data in secondary form
                    value: _('Consultar'),
                    hotkey: 'enter', // Enter key to trigger
                    type: 'form',
                    click: () => {
                        const filterdata = $$('myForm2').getValues(); // Get filter data from the secondary form
                        if (devConfig.getCountryReport()) {
                            filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country based on profile settings
                        }
                        delete filterdata['CountrySelect2']; // Remove unnecessary field
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove date field if not applicable
                            delete filterdata['DateDelete2']; // Remove date delete field if not applicable
                            delete filterdata['DatePay2']; // Remove date pay field if not applicable
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove datepicker if applicable
                                delete filterdata['DateDelete']; // Remove date delete field if applicable
                                delete filterdata['DatePay']; // Remove date pay field if applicable
                            }
                        }
                        filterdata.TypeUser = '1'; // Set user type
                        this.app.callEvent('bonusReport:filter', [filterdata]); // Trigger filter event
                    },
                },
            ],
        };
        const accordion_section = {
            // Accordion section for additional details
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: true,
                    paddingY: 10,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Detalles'), // Header for the accordion section
                            body: {
                                padding: 10,
                                rows: [upper_section2], // Including upper section 2
                            },
                        },
                    ],
                },
            ],
        };
        const accordion_section2 = {
            // Main accordion section configuration
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: false,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Header for the filters section
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [
                                    upper_section, // Including upper section
                                    accordion_section, // Including accordion section
                                    buttons, // Including buttons
                                ],
                            },
                        },
                    ],
                },
            ],
        };
        const accordion_section3 = {
            // Scrollable accordion section configuration
            view: 'scrollview',
            scroll: 'xy',
            body: {
                padding: 10,
                rows: [upper_section, accordion_section], // Including upper section and accordion section
            },
        };
        const filterPopup = webix.ui({
            // UI configuration for filter popup
            view: 'window',
            move: true,
            id: 'filterPopup',
            fullscreen: true, // Fullscreen mode
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Filtros'), align: 'center' }, // Label for the popup header
                    {
                        view: 'icon',
                        icon: 'wxi-close',
                        click: function () {
                            this.getTopParentView().hide(); // Close the window on click
                        },
                    },
                ],
            },
            modal: true, // Modal window
            position: 'center', // Center position for the window
            body: {
                view: 'form',
                id: 'myForm2', // Form ID for the filter popup
                type: 'clean',
                rows: [accordion_section3, buttons2], // Including accordion section and buttons
                rules: {
                    fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
                },
            },
        });
        return {
            view: 'form',
            id: 'myForm', // Form ID for the main view
            type: 'clean',
            rows: [
                screen.width > 500 && screen.height > 500
                    ? accordion_section2 // Including accordion section if screen size is sufficient
                    : { height: 1, width: 0 }, // Placeholder for smaller screens
            ],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }
    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The grid object passed to the init function.
     */
    init(grid) {
        webix.ready(function () {
            if (screen.width < 500) {
                $$('grav').hide(); // Hide element for smaller screens
            } else {
                $$('grav2').hide(); // Hide alternative element for larger screens
            }
            if (profile.typeDate !== 2) {
                $$('Date').hide(); // Hide date field if not applicable
                $$('Datepicker').show(); // Show datepicker
                $$('DateDelete2').hide(); // Hide secondary delete date field
                $$('DateDelete').show(); // Show primary delete date field
                $$('DatePay2').hide(); // Hide secondary payment date field
                $$('DatePay').show(); // Show primary payment date field
            } else {
                if (true) {
                    $$('Date').show(); // Show date field if applicable
                    $$('Datepicker').hide(); // Hide datepicker
                    $$('DateDelete2').show(); // Show secondary delete date field
                    $$('DateDelete').hide(); // Hide primary delete date field
                    $$('DatePay2').show(); // Show secondary payment date field
                    $$('DatePay').hide(); // Hide primary payment date field
                }
            }
        });
        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            devConfig.setDateModal(lang); // Set date modal based on language
        });
    }
}

// FILE DOCUMENTED
